import React, { useState } from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import { STATIC_PAGES } from "../seo/openGraph";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight, Search, PenTool, Code2, Rocket, MessagesSquare, RefreshCw,
  CheckCircle, Clock, Users, Target, Zap, FileText
} from "lucide-react";
import { Button } from "../components/ui/button";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.1, duration: 0.6, ease: "easeOut" },
  }),
};

const steps = [
  {
    num: "01",
    icon: Search,
    title: "Discover",
    tagline: "Understand the problem before solving it.",
    overview: "Name the constraint, the users, and the stack before we write code. Discovery is interviews, a look at what you already have, and an MVP scope you can hold us to.",
    activities: [
      "Stakeholder interviews & vision alignment workshops",
      "Competitive landscape & market opportunity analysis",
      "User research (personas, journey maps, jobs-to-be-done)",
      "Technical audit of existing systems & infrastructure",
      "Feature prioritization & MVP scope definition",
    ],
    deliverables: ["Product Requirements Document", "User Personas & Journey Maps", "Technical Architecture Proposal", "Project Roadmap & Sprint Plan"],
    duration: "1-2 weeks",
    color: "#00AEEF",
  },
  {
    num: "02",
    icon: PenTool,
    title: "Design",
    tagline: "Shape the experience before writing code.",
    overview: "Enough UI to decide. We validate the risky flows with real users before engineering commits. Not a 40-page deck.",
    activities: [
      "Information architecture & navigation design",
      "Low-fidelity wireframes for core user flows",
      "High-fidelity UI design with component library",
      "Interactive prototypes for user testing",
      "Design system creation for scalable development",
    ],
    deliverables: ["Wireframes & Mockups", "Interactive Prototype", "Design System & Component Library", "Usability Test Results"],
    duration: "2-3 weeks",
    color: "#10B981",
  },
  {
    num: "03",
    icon: Code2,
    title: "Develop",
    tagline: "Senior engineers. Two-week slices you can see.",
    overview: "Engineers work with design in two-week sprints. Every sprint ends with a working demo, so you see progress and can change the plan.",
    activities: [
      "Agile/Scrum development in 2-week sprints",
      "Daily standups & transparent sprint dashboards",
      "Code reviews, pair programming & CI/CD pipelines",
      "Automated testing (unit, integration, and E2E)",
      "Sprint demos with stakeholder feedback loops",
    ],
    deliverables: ["Working Software (every sprint)", "Sprint Reports & Burndown Charts", "Automated Test Suite", "API Documentation"],
    duration: "8-20 weeks",
    color: "#8B5CF6",
  },
  {
    num: "04",
    icon: Rocket,
    title: "Deploy",
    tagline: "Real artists ship.",
    overview: "Launch is a checklist: stores, infra, performance, monitoring. We ship with a plan, not crossed fingers.",
    activities: [
      "Production environment setup & hardening",
      "App Store / Google Play submission & optimization",
      "Performance & load testing at scale",
      "Monitoring, alerting & error tracking configuration",
      "Launch checklist execution & go-live support",
    ],
    deliverables: ["Production Deployment", "App Store Listings", "Monitoring Dashboard", "Performance Benchmark Report"],
    duration: "1-2 weeks",
    color: "#F59E0B",
  },
  {
    num: "05",
    icon: MessagesSquare,
    title: "Measure",
    tagline: "Let data tell you what's working.",
    overview: "After launch we instrument events, funnels, and feedback so the next cycle is based on what users did.",
    activities: [
      "Analytics implementation (events, funnels, cohorts)",
      "User feedback collection & NPS surveys",
      "A/B testing framework setup & experiments",
      "Performance monitoring & error rate tracking",
      "Feature usage analysis & adoption metrics",
    ],
    deliverables: ["Analytics Dashboard", "User Feedback Report", "A/B Test Results", "Feature Adoption Analysis"],
    duration: "Ongoing",
    color: "#EC4899",
  },
  {
    num: "06",
    icon: RefreshCw,
    title: "Iterate",
    tagline: "Products are never done. They evolve.",
    overview: "We refine from real usage, not a frozen roadmap. Scope changes when the data says it should.",
    activities: [
      "Backlog grooming & feature prioritization",
      "Performance optimization & technical debt reduction",
      "New feature design, development & deployment",
      "Scalability planning for user growth",
      "Quarterly roadmap reviews & strategy sessions",
    ],
    deliverables: ["Updated Product Roadmap", "Release Notes & Changelogs", "Performance Optimization Report", "Quarterly Business Review"],
    duration: "Ongoing",
    color: "#06B6D4",
  },
];

const stats = [
  { icon: Clock, value: "2-Week", label: "Sprint Cycles" },
  { icon: Users, value: "Dedicated", label: "Pod Teams" },
  { icon: Target, value: "Sprint", label: "Demos" },
  { icon: Zap, value: "Daily", label: "Standups" },
];

export default function ProcessPage() {
  const [activeStep, setActiveStep] = useState(0);
  const current = steps[activeStep];

  return (
    <>
      <Seo {...STATIC_PAGES.process} />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-[#0A0A0A]">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <motion.div variants={fadeUp} initial="hidden" animate="visible">
            <p className="text-[#00AEEF] text-sm uppercase tracking-widest font-medium mb-4">How We Work</p>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-6" data-testid="process-page-heading">
              A Thoughtful Process.
            </h1>
            <p className="text-lg text-neutral-300 max-w-3xl leading-relaxed">
              Start lean, validate fast, and build what you can hold us to. Six phases, demos every sprint. Named work includes AT&T, Microsoft, and BMW; those write-ups live on maxiomtech.com.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Process Stats */}
      <section className="py-12 bg-[#0A0A0A] border-y border-white/5" data-testid="process-stats">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((s, i) => (
              <motion.div
                key={s.label}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i}
                className="flex items-center gap-4"
              >
                <div className="w-12 h-12 rounded-xl bg-[#00AEEF]/10 flex items-center justify-center flex-shrink-0">
                  <s.icon className="w-6 h-6 text-[#00AEEF]" />
                </div>
                <div>
                  <div className="text-xl font-bold text-white">{s.value}</div>
                  <div className="text-xs text-neutral-400 uppercase tracking-widest">{s.label}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive Steps */}
      <section className="py-20 md:py-32 bg-[#0A0A0A]" data-testid="process-interactive">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          {/* Step Selector - Horizontal tabs */}
          <div className="flex flex-wrap gap-2 mb-12" data-testid="process-tabs">
            {steps.map((step, i) => (
              <button
                key={step.num}
                onClick={() => setActiveStep(i)}
                className={`flex items-center gap-2 px-5 py-3 rounded-lg text-sm font-medium transition-all duration-300 ${
                  activeStep === i
                    ? "text-white shadow-lg"
                    : "bg-[#121212] text-gray-400 hover:text-white border border-white/5 hover:border-white/20"
                }`}
                style={activeStep === i ? { backgroundColor: step.color, boxShadow: `0 0 20px ${step.color}40` } : {}}
                data-testid={`process-tab-${i}`}
              >
                <step.icon className="w-4 h-4" />
                <span className="hidden sm:inline">{step.title}</span>
                <span className="sm:hidden">{step.num}</span>
              </button>
            ))}
          </div>

          {/* Step Content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeStep}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
              className="grid grid-cols-1 lg:grid-cols-12 gap-10"
            >
              {/* Left - Main content */}
              <div className="lg:col-span-7">
                <div className="flex items-center gap-4 mb-6">
                  <div
                    className="w-14 h-14 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${current.color}15` }}
                  >
                    <current.icon className="w-7 h-7" style={{ color: current.color }} />
                  </div>
                  <div>
                    <span className="text-xs uppercase tracking-widest font-medium" style={{ color: current.color }}>
                      Phase {current.num}
                    </span>
                    <h2 className="text-3xl md:text-4xl font-bold text-white">{current.title}</h2>
                  </div>
                </div>

                <p className="text-xl text-gray-300 font-medium mb-4 italic">{current.tagline}</p>
                <p className={`text-gray-400 leading-relaxed ${activeStep === 0 ? "mb-4" : "mb-8"}`}>{current.overview}</p>
                {activeStep === 0 && (
                  <p className="text-sm text-gray-500 leading-relaxed mb-8">
                    Read{" "}
                    <Link
                      to="/blog/scoping-first-app-release"
                      className="text-[#00AEEF] hover:text-white transition-colors"
                      data-testid="process-first-release-link"
                    >
                      what to cut from a first release
                    </Link>{" "}
                    before anyone prices the scope.
                  </p>
                )}

                <h3 className="text-white font-semibold text-sm uppercase tracking-widest mb-4">What We Do</h3>
                <ul className="space-y-3 mb-8">
                  {current.activities.map((a, i) => (
                    <motion.li
                      key={i}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.08 }}
                      className="flex items-start gap-3"
                      data-testid={`activity-${i}`}
                    >
                      <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: current.color }} />
                      <span className="text-gray-300 text-sm">{a}</span>
                    </motion.li>
                  ))}
                </ul>
              </div>

              {/* Right - Deliverables & meta */}
              <div className="lg:col-span-5 space-y-6">
                <div className="bg-[#121212] border border-white/5 rounded-xl p-6" data-testid="process-deliverables">
                  <h3 className="text-white font-semibold text-sm uppercase tracking-widest mb-4 flex items-center gap-2">
                    <FileText className="w-4 h-4" style={{ color: current.color }} /> Deliverables
                  </h3>
                  <ul className="space-y-3">
                    {current.deliverables.map((d, i) => (
                      <motion.li
                        key={i}
                        initial={{ opacity: 0, x: 10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.1 }}
                        className="flex items-center gap-3 text-sm"
                      >
                        <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: current.color }} />
                        <span className="text-gray-300">{d}</span>
                      </motion.li>
                    ))}
                  </ul>
                </div>

                <div className="bg-[#121212] border border-white/5 rounded-xl p-6">
                  <h3 className="text-white font-semibold text-sm uppercase tracking-widest mb-3 flex items-center gap-2">
                    <Clock className="w-4 h-4" style={{ color: current.color }} /> Typical Duration
                  </h3>
                  <div className="text-2xl font-bold text-white">{current.duration}</div>
                </div>

                {/* Progress indicator */}
                <div className="bg-[#121212] border border-white/5 rounded-xl p-6">
                  <h3 className="text-white font-semibold text-sm uppercase tracking-widest mb-4">Process Timeline</h3>
                  <div className="space-y-2">
                    {steps.map((s, i) => (
                      <div key={s.num} className="flex items-center gap-3">
                        <div
                          className={`w-3 h-3 rounded-full transition-all ${i <= activeStep ? "" : "opacity-20"}`}
                          style={{ backgroundColor: s.color }}
                        />
                        <span className={`text-xs transition-colors ${i === activeStep ? "text-white font-medium" : i < activeStep ? "text-gray-300" : "text-gray-400"}`}>
                          {s.title}
                        </span>
                        {i === activeStep && (
                          <span className="text-xs px-2 py-0.5 rounded-full ml-auto" style={{ backgroundColor: `${s.color}20`, color: s.color }}>
                            Current
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Navigation */}
                <div className="flex gap-3">
                  <button
                    onClick={() => setActiveStep(Math.max(0, activeStep - 1))}
                    disabled={activeStep === 0}
                    className="flex-1 py-3 rounded-lg bg-[#121212] border border-white/5 text-gray-400 text-sm font-medium disabled:opacity-30 hover:border-white/20 transition-all"
                    data-testid="process-prev"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setActiveStep(Math.min(steps.length - 1, activeStep + 1))}
                    disabled={activeStep === steps.length - 1}
                    className="flex-1 py-3 rounded-lg text-white text-sm font-medium disabled:opacity-30 transition-all"
                    style={{ backgroundColor: current.color }}
                    data-testid="process-next"
                  >
                    Next Phase
                  </button>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* Why Our Process Works */}
      <section className="py-20 md:py-32 bg-[#0f0f0f]" data-testid="process-why">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-16">
            <p className="text-[#00AEEF] text-sm uppercase tracking-widest font-medium mb-4">Why It Works</p>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight">Shipping since 2002</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: "No Surprises", desc: "Sprint demos every two weeks mean you always know exactly where your project stands. No black box development." },
              { title: "Validated Before Built", desc: "We design and test with real users before committing engineering resources, saving time and money on features that don't resonate." },
              { title: "Ship Early, Learn Fast", desc: "MVP-first approach gets your product to market faster, generating real feedback to guide what we build next." },
              { title: "Senior Engineers Only", desc: "No junior handoffs. Your project is built by senior engineers who write the code." },
              { title: "Transparent Communication", desc: "Daily standups, shared Slack channels, sprint dashboards, and weekly stakeholder updates keep everyone aligned." },
              { title: "Built to Scale", desc: "Architecture decisions are made with your growth trajectory in mind, so the codebase scales with your business." },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i}
                className="bg-[#121212] border border-white/5 rounded-xl p-8 hover:border-[#00AEEF]/30 transition-all"
                data-testid={`why-card-${i}`}
              >
                <h3 className="text-lg font-semibold text-white mb-3">{item.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Maxiom family: contextual sister-site equity */}
      <section
        className="py-16 md:py-20 bg-[#0A0A0A] border-t border-white/5"
        data-testid="process-maxiom-family"
      >
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="max-w-3xl mb-10"
          >
            <p className="text-[#00AEEF] text-sm uppercase tracking-widest font-medium mb-4">
              The Maxiom Family
            </p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
              One delivery process. Multiple ways to engage.
            </h2>
            <p className="text-gray-400 leading-relaxed">
              Maxiom Apps is part of{" "}
              <a
                href="https://www.maxiomtech.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#00AEEF] hover:underline"
              >
                Maxiom Technology
              </a>, a family of brands for building, staffing, and leading
              software products. Full builds, dedicated engineers, applied AI
              R&amp;D, or fractional CTO leadership. Same delivery discipline.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              {
                name: "Maxiom Technology",
                href: "https://www.maxiomtech.com",
                blurb: "Enterprise software, digital transformation, and federal-ready delivery.",
              },
              {
                name: "Maxiom Developers",
                href: "https://maxiom.dev",
                blurb: "Staff augmentation and dedicated senior engineering teams.",
              },
              {
                name: "Maxiom Labs",
                href: "https://www.maxiomlabs.com",
                blurb: "Applied AI research, prototypes, and evaluation before production.",
              },
              {
                name: "CTO 2.0",
                href: "https://www.cto20.com",
                blurb: "Fractional CTOs for product strategy, architecture, and hiring.",
              },
            ].map((item, i) => (
              <motion.a
                key={item.href}
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i}
                className="block bg-[#121212] border border-white/5 rounded-xl p-6 hover:border-[#00AEEF]/40 hover:bg-[#00AEEF]/5 transition-all"
                data-testid={`process-family-${i}`}
              >
                <div className="text-white font-semibold mb-2">{item.name}</div>
                <p className="text-gray-500 text-sm leading-relaxed">{item.blurb}</p>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-32 bg-[#0A0A0A]">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl text-center">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="max-w-3xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
              If this is the work, schedule a call.
            </h2>
            <p className="text-gray-400 text-lg mb-10">
              Schedule a discovery call. A senior engineer will walk the actual constraint.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" data-testid="process-cta-schedule">
                <Button className="bg-[#00AEEF] hover:bg-[#0095CC] text-white font-medium px-8 py-6 text-base rounded-md transition-all duration-300 shadow-[0_0_20px_rgba(0,174,239,0.3)] hover:shadow-[0_0_30px_rgba(0,174,239,0.5)]">
                  Schedule a Call <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/case-studies" data-testid="process-cta-cases">
                <Button variant="outline" className="border-white/20 hover:border-[#00AEEF] text-white hover:text-[#00AEEF] px-8 py-6 text-base rounded-md transition-all duration-300">
                  View Case Studies <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
